import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { rupiah, formatDate, formatWIB, wibNowLocal, wibLocalToUTCISO, toNum } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { MoneyInput } from "@/components/MoneyInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { CustomerForm } from "@/pages/Customers";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Plus, Search, Wallet, MessageCircle, Check, Trash2, UserPlus, History, Lock, Unlock, ShieldCheck, AlertCircle, Loader2, Pencil, EyeOff } from "lucide-react";

const SECRET_SESSION_KEY = "debts_secret_unlocked";

const STATUS_LABELS = {
  belum_jatuh_tempo: { label: "Belum jatuh tempo", tone: "secondary" },
  jatuh_tempo_hari_ini: { label: "Jatuh tempo hari ini", tone: "default" },
  terlambat: { label: "Terlambat", tone: "destructive" },
  lunas: { label: "Lunas", tone: "success" },
  aktif: { label: "Aktif", tone: "secondary" },
};
const statusLabel = (s) => (STATUS_LABELS[s] || STATUS_LABELS.aktif).label;

export default function Debts() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({});
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("customer");
  const [openForm, setOpenForm] = useState(params.get("new") === "1");
  const [payDebt, setPayDebt] = useState(null);
  const [histDebt, setHistDebt] = useState(null);
  const [editDebt, setEditDebt] = useState(null);

  // ==== Fitur RAHASIA (khusus Admin; tidak ada di laporan Bang Billah) ====
  const [secretUnlocked, setSecretUnlocked] = useState(() => sessionStorage.getItem(SECRET_SESSION_KEY) === "1");
  const [pwDialog, setPwDialog] = useState(false);
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const openSecret = () => { if (!isAdmin) return; setPw(""); setPwErr(""); setPwDialog(true); };
  const submitPw = async () => {
    setPwBusy(true); setPwErr("");
    try {
      await api.post("/auth/verify-password", { password: pw });
      sessionStorage.setItem(SECRET_SESSION_KEY, "1");
      setSecretUnlocked(true);
      setPwDialog(false);
      toast.success("Mode rahasia aktif");
    } catch {
      setPwErr("Sandi salah.");
    } finally { setPwBusy(false); }
  };
  const lockSecret = () => {
    sessionStorage.removeItem(SECRET_SESSION_KEY);
    setSecretUnlocked(false);
    setPwDialog(false);
    toast.success("Mode rahasia dikunci");
  };
  const showSecret = isAdmin && secretUnlocked;

  const load = () => {
    Promise.all([
      api.get("/debts"),
      api.get("/debts/summary"),
    ]).then(([d, s]) => { setList(d.data); setSummary(s.data); });
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!(await confirmDialog({ title: "Hapus data utang ini?", description: "Seluruh riwayat pembayaran utang ini juga akan dihapus." }))) return;
    await api.delete(`/debts/${id}`);
    toast.success("Utang dihapus"); load();
  };
  const sendTagihan = async (d) => {
    try {
      const r = await api.post(`/debts/${d.id}/send-tagihan`);
      if (r.data.sent) toast.success("Tagihan terkirim via WhatsApp ✓");
      else if (r.data.wa_link) { window.open(r.data.wa_link, "_blank"); toast.success("Membuka WhatsApp Web..."); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim tagihan");
    }
  };
  const togglePublish = async (d, value) => {
    try {
      await api.patch(`/debts/${d.id}/publish`, { is_public: value });
      toast.success(value ? "Hutang ditampilkan di laporan" : "Hutang disembunyikan dari laporan");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status publik");
    }
  };

  const byTab = list.filter((d) => {
    const isPersonal = (d.debt_category || "customer") === "personal";
    if (tab === "personal") return isPersonal;
    if (isPersonal) return false;
    // Tab Pelanggan: hutang tersembunyi (is_public=false) hanya tampil saat Mode Rahasia terbuka
    if (!showSecret && d.is_public === false) return false;
    return true;
  });
  const rows = q
    ? byTab.filter((d) => (d.customer_name || "").toLowerCase().includes(q.toLowerCase()) || (d.customer_phone || "").includes(q))
    : byTab;

  const columns = [
    { key: "created_at", label: "Tanggal & Jam", sortable: true, sortValue: (r) => r.created_at || "", render: (r) => <span className="text-sm">{formatWIB(r.created_at)}</span> },
    { key: "customer_name", label: "Nama", sortable: true, render: (r) => (
      <span className="font-medium">{r.customer_name}
        {r.debt_category === "personal" && <Badge variant="secondary" className="ml-1.5">Pribadi</Badge>}
        {r.is_public === false && <Badge variant="outline" className="ml-1.5 text-muted-foreground gap-1"><EyeOff className="h-3 w-3" /> Tersembunyi</Badge>}
      </span>
    ) },
    { key: "customer_phone", label: "WhatsApp", render: (r) => r.customer_phone ? (
      <span className="text-xs inline-flex items-center gap-1">{r.customer_phone}
        {r.wa_verified
          ? <Badge variant="outline" className="text-emerald-600 border-emerald-200">terverifikasi ✓</Badge>
          : <Badge variant="outline" className="text-amber-600 border-amber-200">belum</Badge>}
      </span>
    ) : <span className="text-xs text-muted-foreground">-</span> },
    { key: "remaining_amount", label: showSecret ? "Sisa (Publis)" : "Sisa", sortable: true, align: "right", sortValue: (r) => Number(r.remaining_amount || 0), render: (r) => r.is_public === false
      ? <span className="text-xs italic text-muted-foreground/50">disembunyikan</span>
      : <span className="money font-semibold text-rose-600 dark:text-rose-400">{rupiah(r.remaining_amount)}</span> },
    { key: "initial_amount", label: showSecret ? "Awal (Publis)" : "Awal", sortable: true, align: "right", sortValue: (r) => Number(r.initial_amount || 0), render: (r) => r.is_public === false
      ? <span className="text-xs italic text-muted-foreground/50">—</span>
      : <span className="money text-muted-foreground">{rupiah(r.initial_amount)}</span> },
    ...(showSecret && tab === "customer" ? [
      { key: "secret_remaining", label: "Rahasia", sortable: true, align: "right", sortValue: (r) => Number(r.secret_remaining || 0), render: (r) => <span className="money font-semibold text-violet-600 dark:text-violet-400">{rupiah(r.secret_remaining)}</span> },
      { key: "selisih", label: "Selisih", sortable: true, align: "right", sortValue: (r) => Number(r.selisih || 0), render: (r) => <span className={`money font-medium ${Number(r.selisih || 0) !== 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>{rupiah(r.selisih)}</span> },
      { key: "is_public", label: "Publik", align: "center", render: (r) => (
        <div className="flex items-center justify-center">
          <Switch data-testid={`switch-public-${r.id}`} checked={r.is_public !== false} onCheckedChange={(v) => togglePublish(r, v)} title={r.is_public !== false ? "Tampil di laporan" : "Disembunyikan dari laporan"} />
        </div>
      ) },
    ] : []),
    { key: "status", label: "Status", sortable: true, filter: true, filterValue: (r) => statusLabel(r.status), render: (r) => {
      const meta = STATUS_LABELS[r.status] || STATUS_LABELS.aktif;
      return <Badge variant={meta.tone === "success" ? "default" : meta.tone === "destructive" ? "destructive" : "secondary"} className={meta.tone === "success" ? "bg-emerald-500 hover:bg-emerald-500" : ""}>{meta.label}</Badge>;
    } },
    { key: "due_date", label: "Jatuh Tempo", sortable: true, sortValue: (r) => r.due_date || "", render: (r) => <span className="text-xs">{r.due_date ? formatDate(r.due_date) : "-"}</span> },
    { key: "_act", label: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <Button data-testid={`btn-pay-${r.id}`} size="sm" variant="outline" onClick={() => setPayDebt(r)} disabled={r.remaining_amount <= 0} className="gap-1"><Wallet className="h-3.5 w-3.5" /> Bayar</Button>
        <Button data-testid={`btn-edit-debt-${r.id}`} size="icon" variant="ghost" onClick={() => setEditDebt(r)} title="Edit / atur nominal publis"><Pencil className="h-3.5 w-3.5" /></Button>
        <Button data-testid={`btn-hist-debt-${r.id}`} size="icon" variant="ghost" onClick={() => setHistDebt(r)} title="Riwayat cicilan"><History className="h-3.5 w-3.5" /></Button>
        {r.customer_phone && r.remaining_amount > 0 && (
          <Button data-testid={`btn-tagihan-${r.id}`} size="icon" variant="outline" onClick={() => sendTagihan(r)} className="hover:bg-emerald-500 hover:text-white hover:border-emerald-500"><MessageCircle className="h-3.5 w-3.5" /></Button>
        )}
        <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`btn-del-debt-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          {/* Trigger tersamarkan: Admin klik label kecil ini utk membuka mode rahasia */}
          <p
            data-testid="debts-secret-trigger"
            onClick={openSecret}
            className={`text-xs uppercase tracking-widest text-muted-foreground font-semibold select-none ${isAdmin ? "cursor-default" : ""}`}
          >
            Piutang &amp; Tagihan
          </p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Utang Pelanggan</h1>
        </div>
        <div className="flex items-center gap-2">
          {showSecret && (
            <Button data-testid="btn-lock-secret" variant="outline" size="sm" onClick={lockSecret} className="gap-1.5 text-violet-600 border-violet-200">
              <ShieldCheck className="h-4 w-4" /> Kunci Rahasia
            </Button>
          )}
          <Button data-testid="btn-new-debt" onClick={() => setOpenForm(true)} className="gap-2 h-11"><Plus className="h-4 w-4" /> Tambah Utang</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Total Piutang{showSecret ? " (Publis)" : ""}</p><p className="mt-1.5 money text-xl font-semibold text-rose-600 dark:text-rose-400">{rupiah(summary.total_piutang || 0)}</p></Card>
        {showSecret ? (
          <>
            <Card className="p-4 border-violet-200 dark:border-violet-900"><p className="text-xs uppercase tracking-widest text-violet-600 font-semibold">Piutang Rahasia</p><p className="mt-1.5 money text-xl font-semibold text-violet-600 dark:text-violet-400">{rupiah(summary.total_piutang_rahasia || 0)}</p></Card>
            <Card className="p-4 border-amber-200 dark:border-amber-900"><p className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Total Selisih</p><p className="mt-1.5 money text-xl font-semibold text-amber-600 dark:text-amber-400">{rupiah(summary.total_selisih || 0)}</p></Card>
          </>
        ) : (
          <>
            <Card className="p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Total Tertagih</p><p className="mt-1.5 money text-xl font-semibold text-emerald-600 dark:text-emerald-400">{rupiah(summary.total_tertagih || 0)}</p></Card>
            <Card className="p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Terlambat</p><p className="mt-1.5 text-xl font-semibold text-rose-600">{summary.terlambat || 0}</p></Card>
          </>
        )}
        <Card className="p-4"><p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Jatuh Tempo Hari Ini</p><p className="mt-1.5 text-xl font-semibold text-amber-600">{summary.jatuh_tempo_hari_ini || 0}</p></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="debt-tab-customer" value="customer">Pelanggan</TabsTrigger>
          <TabsTrigger data-testid="debt-tab-personal" value="personal">Pribadi</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="search-debts" placeholder="Cari nama / HP..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      <Card className="p-4">
        <DataTable columns={columns} rows={rows} testid="debt-table" emptyText="Belum ada data utang" />
      </Card>

      <DebtForm open={openForm} tab={tab} showSecret={showSecret} allDebts={list} onClose={() => setOpenForm(false)} onSaved={load} />
      <EditDebtDialog debt={editDebt} showSecret={showSecret} onClose={() => setEditDebt(null)} onSaved={load} />
      <PayDialog debt={payDebt} onClose={() => setPayDebt(null)} onSaved={load} />
      <DebtHistoryDialog debt={histDebt} showSecret={showSecret} onClose={() => setHistDebt(null)} />

      {/* Dialog sandi mode rahasia */}
      <Dialog open={pwDialog} onOpenChange={(o) => { if (!o) setPwDialog(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Buka Mode Rahasia</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Masukkan sandi akun untuk melihat nilai utang rahasia &amp; selisih.</p>
            <Input
              data-testid="debts-secret-pw"
              type="password"
              autoFocus
              placeholder="Sandi akun"
              value={pw}
              onChange={(e) => { setPw(e.target.value); setPwErr(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw) submitPw(); }}
            />
            {pwErr && <p className="text-xs text-rose-600 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> {pwErr}</p>}
            <DialogFooter>
              <Button data-testid="btn-submit-secret-debt" onClick={submitPw} disabled={pwBusy || !pw} className="w-full gap-2">
                {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />} Buka
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditDebtDialog({ debt, showSecret = false, onClose, onSaved }) {
  const [form, setForm] = useState({ initial_amount: "", secret_amount: "", is_public: true, due_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const isPersonal = (debt?.debt_category || "customer") === "personal";
  const canSecret = showSecret && !isPersonal;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (debt) setForm({
      initial_amount: debt.initial_amount ?? "",
      secret_amount: debt.secret_amount ?? "",
      is_public: debt.is_public !== false,
      due_date: debt.due_date || "",
      notes: debt.notes || "",
    });
  }, [debt]);

  const save = async () => {
    if (form.initial_amount === "" || form.initial_amount == null) return toast.error("Isi nominal publis");
    setSaving(true);
    try {
      const payload = {
        customer_id: debt.customer_id,
        customer_name: debt.customer_name,
        initial_amount: toNum(form.initial_amount),
        due_date: form.due_date || undefined,
        notes: form.notes || undefined,
      };
      if (canSecret) {
        if (form.secret_amount !== "" && form.secret_amount != null) payload.secret_amount = toNum(form.secret_amount);
        payload.is_public = !!form.is_public;
      }
      await api.patch(`/debts/${debt.id}`, payload);
      toast.success("Utang diperbarui"); onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui");
    } finally { setSaving(false); }
  };

  const publik = toNum(form.initial_amount || 0);
  const rahasia = canSecret ? toNum(form.secret_amount || 0) : publik;
  const showFull = canSecret && form.secret_amount !== "" && publik === rahasia;
  const setShowFull = (checked) => {
    if (checked) set("initial_amount", form.secret_amount);
  };

  return (
    <Dialog open={!!debt} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Utang: {debt?.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {canSecret ? (
            <Card className="p-3 border-violet-200 dark:border-violet-900 space-y-3">
              <p className="text-[11px] uppercase tracking-widest text-violet-600 font-semibold flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Atur Rahasia &amp; Publis</p>
              <div className="flex items-center justify-between rounded-md bg-accent/40 px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Tampilkan di laporan (Publik)</p>
                  <p className="text-[11px] text-muted-foreground">Matikan untuk menyembunyikan hutang ini dari laporan &amp; saldo.</p>
                </div>
                <Switch data-testid="edit-is-public" checked={!!form.is_public} onCheckedChange={(v) => set("is_public", v)} />
              </div>
              <div>
                <Label>Hutang Asli (Rahasia)</Label>
                <MoneyInput data-testid="edit-secret-amount" value={form.secret_amount} onChange={(v) => set("secret_amount", v)} />
                <p className="text-[11px] text-muted-foreground mt-1">Nominal sebenarnya. Hanya Anda (Admin) yang bisa melihat.</p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <Checkbox data-testid="edit-show-full" checked={showFull} onCheckedChange={setShowFull} disabled={form.secret_amount === "" || !form.is_public} />
                Tampilkan nominal penuh (publis = asli)
              </label>
              <div>
                <Label>Ditampilkan / Publis</Label>
                <MoneyInput data-testid="edit-public-amount" value={form.initial_amount} onChange={(v) => set("initial_amount", v)} />
                <p className="text-[11px] text-muted-foreground mt-1">Nominal yang muncul di laporan &amp; mempengaruhi saldo.</p>
              </div>
              {form.is_public
                ? <p className="text-xs text-amber-600 font-medium">Selisih tersembunyi: {rupiah(rahasia - publik)}</p>
                : <p className="text-xs text-muted-foreground italic flex items-center gap-1.5"><EyeOff className="h-3.5 w-3.5" /> Hutang ini disembunyikan — kontribusi ke laporan = Rp 0 (seluruh {rupiah(rahasia)} tersembunyi).</p>}
            </Card>
          ) : (
            <div>
              <Label>Nominal {isPersonal ? "" : "(Publis)"}</Label>
              <MoneyInput data-testid="edit-public-amount" value={form.initial_amount} onChange={(v) => set("initial_amount", v)} />
              {!isPersonal && <p className="text-[11px] text-muted-foreground mt-1">Buka Mode Rahasia untuk mengatur nilai asli vs publis.</p>}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">Cicilan yang sudah dibayar tetap terjaga; sisa dihitung ulang otomatis.</p>
          <div className="grid grid-cols-1 gap-3">
            <div><Label>Jatuh Tempo</Label><Input data-testid="edit-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
            <div><Label>Catatan</Label><Textarea data-testid="edit-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-edit-debt" onClick={save} disabled={saving} className="flex-1">Simpan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DebtForm({ open, tab = "customer", showSecret = false, allDebts = [], onClose, onSaved }) {
  const empty = { customer_id: "", initial_amount: "", secret_amount: "", is_public: true, due_date: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [dt, setDt] = useState(wibNowLocal());
  const [customers, setCustomers] = useState([]);
  const [openCustForm, setOpenCustForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { if (open) { setForm(empty); setDt(wibNowLocal()); loadCustomers(); } /* eslint-disable-next-line */ }, [open]);

  const selected = customers.find((c) => c.id === form.customer_id);
  // Hutang AKTIF (sisa > 0) milik pelanggan terpilih — penambahan akan digabung ke sini.
  const existingOpen = (allDebts || []).filter((d) => d.customer_id === form.customer_id && Number(d.remaining_amount || 0) > 0);
  const existingDebt = existingOpen[0] || null;
  const existingRemaining = existingOpen.reduce((s, d) => s + Number(d.remaining_amount || 0), 0);
  // Pilihan pelanggan mengikuti tab aktif (Pelanggan vs Pribadi)
  const custOptions = customers.filter((c) =>
    tab === "personal" ? c.category === "personal" : (c.category || "customer") !== "personal"
  );
  // Field rahasia hanya untuk hutang customer & saat mode rahasia terbuka
  const isCustomerDebt = tab === "customer" && (!selected || (selected.category || "customer") !== "personal");
  const secretEnabled = showSecret && isCustomerDebt;

  const save = async () => {
    if (!form.customer_id) return toast.error("Pilih pelanggan terlebih dahulu");
    if (!form.initial_amount) return toast.error("Isi nominal");
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        customer_name: selected?.name || "",
        initial_amount: toNum(form.initial_amount),
        due_date: form.due_date,
        notes: form.notes,
        date: wibLocalToUTCISO(dt),
      };
      if (secretEnabled && form.secret_amount !== "" && form.secret_amount != null) {
        payload.secret_amount = toNum(form.secret_amount);
      }
      if (secretEnabled) payload.is_public = !!form.is_public;
      const r = await api.post("/debts", payload);
      toast.success(r.data?.merged ? "Ditambahkan ke hutang yang sudah ada ✓" : "Utang dicatat"); onSaved?.(); onClose();
    } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Utang Baru {tab === "personal" ? "· Pribadi" : "· Pelanggan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{tab === "personal" ? "Pribadi (Muis / Bang Billah)" : "Pelanggan"}</Label>
              <div className="flex gap-2">
                <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                  <SelectTrigger data-testid="debt-customer" className="flex-1"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                  <SelectContent>
                    {custOptions.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Belum ada data</div>}
                    {custOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" data-testid="btn-quick-add-customer" onClick={() => setOpenCustForm(true)} title="Tambah baru"><UserPlus className="h-4 w-4" /></Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Nama & nomor WA diambil dari data Pelanggan. Nomor WA diverifikasi terdaftar/tidak saat pelanggan dibuat.</p>
            </div>

            {selected && (
              <Card className="p-3 bg-accent/40 text-sm space-y-0.5">
                <p className="font-semibold flex items-center gap-1.5">{selected.name} <Badge variant="secondary">{selected.category === "personal" ? "Pribadi" : "Customer"}</Badge></p>
                {selected.phone && <p className="text-xs text-muted-foreground inline-flex items-center gap-1">WA: {selected.phone} {selected.wa_verified ? <span className="text-emerald-600">✓ terverifikasi</span> : <span className="text-amber-600">belum terverifikasi</span>}</p>}
                {selected.address && <p className="text-xs text-muted-foreground">Alamat: {selected.address}</p>}
              </Card>
            )}

            {selected && existingDebt && (
              <Card className="p-3 border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-500/10 space-y-2" data-testid="debt-existing-info">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-widest text-amber-700 dark:text-amber-400 font-semibold flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Hutang Aktif Sebelumnya</p>
                  <span className="money text-sm font-bold text-rose-600 dark:text-rose-400">{rupiah(existingRemaining)}</span>
                </div>
                <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-snug">
                  Nominal baru akan <b>DITAMBAHKAN</b> ke hutang aktif ini (bukan baris baru).
                </p>
                {(existingDebt.additions?.length ? existingDebt.additions : [{ id: "base", amount: existingDebt.initial_amount, date: existingDebt.created_at, notes: existingDebt.notes, base: true }]).map((a, i) => (
                  <div key={a.id || i} className="flex items-center justify-between text-xs border-t border-amber-200/70 dark:border-amber-800/60 pt-1.5">
                    <span className="text-muted-foreground">{formatWIB(a.date || a.created_at)}{a.notes ? ` · ${a.notes}` : ""}</span>
                    <span className="money font-medium">{rupiah(a.amount)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs pt-1">
                  <span className="text-muted-foreground">Awal total</span>
                  <span className="money">{rupiah(existingDebt.initial_amount)}</span>
                </div>
              </Card>
            )}

            {secretEnabled ? (
              <Card className="p-3 border-violet-200 dark:border-violet-900 space-y-3">
                <p className="text-[11px] uppercase tracking-widest text-violet-600 font-semibold flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Mode Rahasia</p>
                <div className="flex items-center justify-between rounded-md bg-accent/40 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">Tampilkan di laporan (Publik)</p>
                    <p className="text-[11px] text-muted-foreground">Matikan untuk menyembunyikan hutang ini dari laporan.</p>
                  </div>
                  <Switch data-testid="debt-is-public" checked={!!form.is_public} onCheckedChange={(v) => set("is_public", v)} />
                </div>
                <div>
                  <Label>Hutang Asli (Rahasia)</Label>
                  <MoneyInput data-testid="debt-secret-amount" value={form.secret_amount} onChange={(v) => set("secret_amount", v)} />
                  <p className="text-[11px] text-muted-foreground mt-1">Nominal sebenarnya. Hanya Anda (Admin) yang bisa melihat. Kosongkan = sama dengan Publis.</p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Checkbox data-testid="debt-show-full" checked={form.secret_amount !== "" && toNum(form.initial_amount || 0) === toNum(form.secret_amount || 0)} onCheckedChange={(c) => { if (c) set("initial_amount", form.secret_amount); }} disabled={form.secret_amount === "" || !form.is_public} />
                  Tampilkan nominal penuh (publis = asli)
                </label>
                <div>
                  <Label>Ditampilkan / Publis</Label>
                  <MoneyInput data-testid="debt-amount" value={form.initial_amount} onChange={(v) => set("initial_amount", v)} />
                  <p className="text-[11px] text-muted-foreground mt-1">Nominal yang muncul di laporan &amp; mempengaruhi saldo.</p>
                </div>
                {!form.is_public
                  ? <p className="text-xs text-muted-foreground italic flex items-center gap-1.5"><EyeOff className="h-3.5 w-3.5" /> Hutang ini akan disembunyikan dari laporan.</p>
                  : (form.secret_amount !== "" && form.initial_amount !== "" && (
                      <p className="text-xs text-amber-600 font-medium">Selisih tersembunyi: {rupiah(toNum(form.secret_amount) - toNum(form.initial_amount))}</p>
                    ))}
              </Card>
            ) : (
              <div><Label>Nominal</Label><MoneyInput data-testid="debt-amount" value={form.initial_amount} onChange={(v) => set("initial_amount", v)} /></div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jatuh Tempo</Label><Input data-testid="debt-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></div>
              <div>
                <Label>Tanggal & Jam (WIB)</Label>
                <Input data-testid="debt-datetime" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} />
              </div>
            </div>
            <div><Label>Catatan</Label><Textarea data-testid="debt-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
              <Button data-testid="btn-save-debt" onClick={save} disabled={saving} className="flex-1">{existingDebt ? "Tambah ke Hutang" : "Simpan"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CustomerForm open={openCustForm} customer={null} defaultCategory={tab} onClose={() => setOpenCustForm(false)} onSaved={async (saved) => { await loadCustomers(); if (saved?.id) set("customer_id", saved.id); }} />
    </>
  );
}

function DebtHistoryDialog({ debt, showSecret = false, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!debt) return;
    setLoading(true);
    api.get(`/debts/${debt.id}/payments`).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [debt]);
  const totalPaid = rows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const secretVisible = showSecret && debt && (debt.debt_category || "customer") !== "personal";
  return (
    <Dialog open={!!debt} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Riwayat Cicilan: {debt?.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Utang Awal</p><p className="money text-sm font-semibold">{rupiah(debt?.initial_amount || 0)}</p></Card>
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Dibayar</p><p className="money text-sm font-semibold text-emerald-600">{rupiah(totalPaid)}</p></Card>
            <Card className="p-3"><p className="text-[11px] uppercase text-muted-foreground font-semibold">Sisa</p><p className="money text-sm font-semibold text-rose-600">{rupiah(debt?.remaining_amount || 0)}</p></Card>
          </div>
          {secretVisible && (
            <div className="grid grid-cols-3 gap-2">
              <Card className="p-3 border-violet-200 dark:border-violet-900"><p className="text-[11px] uppercase text-violet-600 font-semibold">Asli (Rahasia)</p><p className="money text-sm font-semibold text-violet-600">{rupiah(debt?.secret_amount || 0)}</p></Card>
              <Card className="p-3 border-violet-200 dark:border-violet-900"><p className="text-[11px] uppercase text-violet-600 font-semibold">Sisa Rahasia</p><p className="money text-sm font-semibold text-violet-600">{rupiah(debt?.secret_remaining || 0)}</p></Card>
              <Card className="p-3 border-amber-200 dark:border-amber-900"><p className="text-[11px] uppercase text-amber-600 font-semibold">Selisih</p><p className="money text-sm font-semibold text-amber-600">{rupiah(debt?.selisih || 0)}</p></Card>
            </div>
          )}
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Memuat...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Belum ada cicilan.</p>
          ) : (
            <div className="divide-y border rounded-lg" data-testid="debt-history-list">
              {rows.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium">{formatWIB(p.created_at)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{p.method || "tunai"}{p.notes ? ` · ${p.notes}` : ""}</p>
                  </div>
                  <span className="money font-semibold text-emerald-600 dark:text-emerald-400">+{rupiah(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" onClick={onClose} className="w-full">Tutup</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ debt, onClose, onSaved }) {
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("tunai");
  const [notes, setNotes] = useState("");
  useEffect(() => { setAmount(debt?.remaining_amount || 0); setNotes(""); }, [debt]);
  const save = async () => {
    if (!amount) return toast.error("Isi nominal");
    await api.post(`/debts/${debt.id}/payment`, { amount: toNum(amount), method, notes });
    toast.success("Pembayaran tercatat"); onSaved?.(); onClose();
  };
  return (
    <Dialog open={!!debt} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Bayar Utang: {debt?.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Card className="p-3 bg-accent/40">
            <p className="text-xs text-muted-foreground">Sisa utang</p>
            <p className="money text-2xl font-bold text-rose-600 dark:text-rose-400">{rupiah(debt?.remaining_amount || 0)}</p>
          </Card>
          <div><Label>Nominal Bayar</Label><MoneyInput data-testid="pay-amount" value={amount} onChange={setAmount} /></div>
          <div>
            <Label>Metode</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tunai">Tunai</SelectItem>
                <SelectItem value="transfer">Transfer</SelectItem>
                <SelectItem value="qris">QRIS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Catatan</Label><Input data-testid="pay-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-pay" onClick={save} className="flex-1 gap-2"><Check className="h-4 w-4" /> Bayar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
